<template>
    <v-dialog v-model="internalShow" max-width="900" persistent>
        <v-card>
            <v-card-title>{{ $t('routePlanning.dialogs.historico.loadTitle') }}</v-card-title>
            <v-card-text>
                <v-row dense class="mb-2" align="center">
                    <v-col cols="12" sm="5">
                        <v-menu
                            v-model="menuDateFrom"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    :value="dateFromDisplay"
                                    :label="$t('datetime.startDate')"
                                    prepend-inner-icon="mdi-calendar"
                                    readonly
                                    clearable
                                    dense
                                    hide-details="auto"
                                    v-bind="attrs"
                                    v-on="on"
                                    @click:clear.stop="clearDateFrom"
                                />
                            </template>
                            <v-date-picker
                                v-model="dateFrom"
                                no-title
                                scrollable
                                :locale="datePickerLocale"
                                :max="dateTo || undefined"
                                @input="menuDateFrom = false"
                            />
                        </v-menu>
                    </v-col>
                    <v-col cols="12" sm="5">
                        <v-menu
                            v-model="menuDateTo"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    :value="dateToDisplay"
                                    :label="$t('datetime.finalDate')"
                                    prepend-inner-icon="mdi-calendar"
                                    readonly
                                    clearable
                                    dense
                                    hide-details="auto"
                                    v-bind="attrs"
                                    v-on="on"
                                    @click:clear.stop="clearDateTo"
                                />
                            </template>
                            <v-date-picker
                                v-model="dateTo"
                                no-title
                                scrollable
                                :locale="datePickerLocale"
                                :min="dateFrom || undefined"
                                @input="menuDateTo = false"
                            />
                        </v-menu>
                    </v-col>
                    <v-col cols="12" sm="2" class="d-flex justify-end">
                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    color="primary"
                                    dark
                                    small
                                    fab
                                    :loading="loading"
                                    v-bind="attrs"
                                    v-on="on"
                                    @click="fetchList"
                                >
                                    <v-icon>mdi-magnify</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ $t('routePlanning.dialogs.historico.filter') }}</span>
                        </v-tooltip>
                    </v-col>
                </v-row>
                <merus-data-table
                    :headers="headers"
                    :items="items"
                    :loading="loading"
                    :default-multi-sort="defaultMultiSort"
                    item-key="id_historico_roteirizacao"
                    show-select
                    single-select
                    hide-select-all
                    v-model="selected"
                >
                    <template v-slot:item.tipo_evento_extenso="{ item }">
                        <v-chip x-small label outlined>
                            {{ item.tipo_evento_extenso }}
                        </v-chip>
                    </template>
                    <template v-slot:item.km_total_ordem="{ item }">
                        {{ item.km_total_extenso }}
                    </template>
                    <template v-slot:item.custo_total_ordem="{ item }">
                        {{ item.custo_total_extenso }}
                    </template>
                    <template v-slot:item.custo_motoristas_ordem="{ item }">
                        {{ item.custo_motoristas_extenso }}
                    </template>
                </merus-data-table>
            </v-card-text>
            <v-card-actions class="flex-wrap">
                <v-tooltip top :disabled="canLoadLatest">
                    <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on" class="d-inline-block mb-1">
                            <v-btn
                                color="primary"
                                outlined
                                :loading="loadingLatest || resolvingLatest"
                                :disabled="!canLoadLatest || loading || loadingDetail || resolvingLatest"
                                @click="confirmLoadLatest"
                            >
                                <v-icon left small>mdi-history</v-icon>
                                {{ $t('routePlanning.dialogs.historico.loadLatest') }}
                            </v-btn>
                        </span>
                    </template>
                    <span>{{ loadLatestDisabledHint }}</span>
                </v-tooltip>
                <v-spacer />
                <v-btn text color="primary" class="mb-1" @click="close">{{ $t('button.cancel') }}</v-btn>
                <v-btn
                    color="primary"
                    class="mb-1"
                    :disabled="!selected.length || loadingDetail || loadingLatest"
                    :loading="loadingDetail"
                    @click="confirmLoad"
                >
                    {{ $t('routePlanning.actions.loadHistorico') }}
                </v-btn>
            </v-card-actions>
        </v-card>

        <v-dialog v-model="confirmReplace" max-width="480" persistent>
            <v-card>
                <v-card-title>{{ $t('routePlanning.dialogs.historico.confirmReplaceTitle') }}</v-card-title>
                <v-card-text>{{ $t('routePlanning.dialogs.historico.confirmReplaceMessage') }}</v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="cancelConfirmReplace">{{ $t('button.cancel') }}</v-btn>
                    <v-btn color="primary" dark @click="doConfirmReplace">{{ $t('button.confirm') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-dialog>
</template>

<script>
import apiController from '@/utils/apiController';
import MerusDataTable from '@/components/Tables/MerusDataTable.vue';

export default {
    name: 'RoteirizacaoHistoricoLoadDialog',
    components: { MerusDataTable },
    props: {
        value: { type: Boolean, default: false },
        token: { type: String, default: '' },
        idUsuario: { type: [Number, String], default: null },
        idCercaDestino: { type: [Number, String], default: null },
        idUnidade: { type: [Number, String], default: null },
        hasRoutingSolution: { type: Boolean, default: false },
        /** Versão atualmente carregada na tela; botão "última versão" só se intermediária. */
        currentHistoricoId: { type: [Number, String], default: null },
    },
    data() {
        return {
            items: [],
            loading: false,
            loadingDetail: false,
            loadingLatest: false,
            resolvingLatest: false,
            latestHistoricoId: null,
            selected: [],
            confirmReplace: false,
            pendingId: null,
            pendingRecord: null,
            pendingMode: null,
            dateFrom: null,
            dateTo: null,
            menuDateFrom: false,
            menuDateTo: false,
        };
    },
    computed: {
        internalShow: {
            get() { return this.value; },
            set(v) { this.$emit('input', v); },
        },
        resolvedDestinoId() {
            const fromProp = Number(this.idCercaDestino);
            if (Number.isFinite(fromProp) && fromProp > 0) return fromProp;
            const first = this.items && this.items[0];
            const fromItem = first && first.id_cerca_destino != null
                ? Number(first.id_cerca_destino)
                : NaN;
            return Number.isFinite(fromItem) && fromItem > 0 ? fromItem : null;
        },
        hasCurrentHistorico() {
            return this.currentHistoricoId != null && String(this.currentHistoricoId).trim() !== '';
        },
        isOnIntermediateVersion() {
            if (!this.hasCurrentHistorico || this.latestHistoricoId == null) return false;
            return String(this.currentHistoricoId) !== String(this.latestHistoricoId);
        },
        canLoadLatest() {
            if (this.resolvingLatest) return false;
            if (!this.isOnIntermediateVersion) return false;
            if (this.resolvedDestinoId != null) return true;
            return Array.isArray(this.items) && this.items.length > 0;
        },
        loadLatestDisabledHint() {
            if (!this.hasCurrentHistorico) {
                return this.$t('routePlanning.dialogs.historico.loadLatestNeedIntermediate');
            }
            if (this.resolvingLatest) {
                return this.$t('routePlanning.dialogs.historico.loadLatest');
            }
            if (this.latestHistoricoId != null && !this.isOnIntermediateVersion) {
                return this.$t('routePlanning.dialogs.historico.loadLatestAlreadyLatest');
            }
            if (this.resolvedDestinoId == null && !(this.items && this.items.length)) {
                return this.$t('routePlanning.dialogs.historico.loadLatestNeedDestination');
            }
            return this.$t('routePlanning.dialogs.historico.loadLatestNeedIntermediate');
        },
        datePickerLocale() {
            return this.$i18n.locale === 'es-MX' ? 'es-MX' : 'pt-BR';
        },
        dateFromDisplay() {
            return this.formatDateOnly(this.dateFrom);
        },
        dateToDisplay() {
            return this.formatDateOnly(this.dateTo);
        },
        headers() {
            return [
                { text: this.$t('routePlanning.dialogs.historico.columns.title'), value: 'titulo', sortable: true, filtrable: true },
                { text: this.$t('routePlanning.dialogs.historico.columns.eventType'), value: 'tipo_evento_extenso', width: '130px', sortable: true, filtrable: true },
                { text: this.$t('routePlanning.dialogs.historico.columns.version'), value: 'versao', width: '80px', sortable: true, filtrable: true },
                { text: this.$t('routePlanning.dialogs.historico.columns.destination'), value: 'nome_destino', sortable: true, filtrable: true },
                { text: this.$t('routePlanning.dialogs.historico.columns.mode'), value: 'modo_extenso', width: '120px', sortable: true, filtrable: true },
                { text: this.$t('routePlanning.dialogs.historico.columns.date'), value: 'dt_calculo_extenso', width: '160px', sortable: true, filtrable: true },
                { text: this.$t('routePlanning.dialogs.historico.columns.km'), value: 'km_total_ordem', width: '90px', sortable: true, filtrable: true },
                { text: this.$t('routePlanning.dialogs.historico.columns.cost'), value: 'custo_total_ordem', width: '110px', sortable: true, filtrable: true },
                { text: this.$t('routePlanning.dialogs.historico.columns.driversCost'), value: 'custo_motoristas_ordem', width: '130px', sortable: true, filtrable: true },
            ];
        },
        defaultMultiSort() {
            return [
                { value: 'dt_calculo_ordem', options: { text: this.$t('system.labels.decreasing'), value: -1 } },
            ];
        },
    },
    watch: {
        value(open) {
            if (open) {
                this.selected = [];
                this.dateFrom = null;
                this.dateTo = null;
                this.latestHistoricoId = null;
                this.fetchList();
            }
        },
    },
    methods: {
        close() {
            this.internalShow = false;
        },
        /**
         * Descobre o id do histórico mais recente do destino/unidade
         * para saber se a versão atual na tela é intermediária.
         */
        async resolveLatestHistoricoId() {
            this.latestHistoricoId = null;
            if (!this.hasCurrentHistorico) return;
            this.resolvingLatest = true;
            try {
                const destinoId = this.resolvedDestinoId;
                if (destinoId != null) {
                    const query = { id_cerca_destino: destinoId };
                    if (this.idUnidade != null && String(this.idUnidade).trim() !== '') {
                        query.id_unidade = this.idUnidade;
                    }
                    if (this.idUsuario != null) query.id_usuario = this.idUsuario;
                    const res = await apiController.request(
                        apiController.endpoints.getUltimohistoricoroteirizacao,
                        { query, silent: true },
                    );
                    if (res.data && res.data.id_historico_roteirizacao != null) {
                        this.latestHistoricoId = res.data.id_historico_roteirizacao;
                        return;
                    }
                }
                // Fallback: primeiro item da listagem (ordenada por data desc).
                const first = this.items && this.items[0];
                if (first && first.id_historico_roteirizacao != null) {
                    this.latestHistoricoId = first.id_historico_roteirizacao;
                }
            } catch (e) {
                const first = this.items && this.items[0];
                if (first && first.id_historico_roteirizacao != null) {
                    this.latestHistoricoId = first.id_historico_roteirizacao;
                }
            } finally {
                this.resolvingLatest = false;
            }
        },
        clearDateFrom() {
            this.dateFrom = null;
        },
        clearDateTo() {
            this.dateTo = null;
        },
        formatDateOnly(raw) {
            if (!raw) return '';
            const parts = String(raw).split('-');
            if (parts.length !== 3) return String(raw);
            const [y, m, d] = parts;
            return `${d}/${m}/${y}`;
        },
        formatDate(raw) {
            if (!raw) return '—';
            const d = new Date(raw);
            if (Number.isNaN(d.getTime())) return String(raw);
            return d.toLocaleString(this.$i18n.locale === 'es-MX' ? 'es-MX' : 'pt-BR');
        },
        formatKm(v) {
            const n = Number(v);
            return Number.isFinite(n) ? `${n.toFixed(2)} km` : '—';
        },
        formatCost(v) {
            const n = Number(v);
            return Number.isFinite(n) ? n.toLocaleString(this.$i18n.locale === 'es-MX' ? 'es-MX' : 'pt-BR', {
                style: 'currency',
                currency: 'BRL',
            }) : '—';
        },
        modeLabel(id) {
            const n = Number(id);
            if (n === 1) return this.$t('routePlanning.fields.routingModeOperational');
            if (n === 2) return this.$t('routePlanning.fields.routingModeSimulation');
            if (n === 3) return this.$t('routePlanning.fields.routingModePreview');
            return String(id || '—');
        },
        tipoEventoLabel(tipo) {
            const key = String(tipo || 'MANUAL').toUpperCase();
            const path = `routePlanning.dialogs.historico.eventTypes.${key}`;
            const label = this.$t(path);
            return label === path ? key : label;
        },
        mapHistoricoItem(row) {
            if (!row) return row;
            const dt = row.dt_calculo ? new Date(row.dt_calculo) : null;
            const km = Number(row.km_total);
            const custo = row.custo_total != null ? Number(row.custo_total) : null;
            const custoMotoristas = row.custo_motoristas_total != null ? Number(row.custo_motoristas_total) : null;
            return {
                ...row,
                tipo_evento_extenso: this.tipoEventoLabel(row.tipo_evento),
                modo_extenso: this.modeLabel(row.id_modo_roteirizacao),
                dt_calculo_extenso: this.formatDate(row.dt_calculo),
                dt_calculo_ordem: dt && !Number.isNaN(dt.getTime()) ? dt.getTime() : 0,
                km_total_ordem: Number.isFinite(km) ? km : 0,
                km_total_extenso: this.formatKm(row.km_total),
                custo_total_ordem: Number.isFinite(custo) ? custo : 0,
                custo_total_extenso: this.formatCost(row.custo_total),
                custo_motoristas_ordem: Number.isFinite(custoMotoristas) ? custoMotoristas : 0,
                custo_motoristas_extenso: custoMotoristas != null ? this.formatCost(row.custo_motoristas_total) : '—',
            };
        },
        isValidDateRange() {
            if (!this.dateFrom || !this.dateTo) return true;
            return this.dateFrom <= this.dateTo;
        },
        async fetchList() {
            if (!this.isValidDateRange()) {
                this.$emit('error', this.$t('routePlanning.dialogs.historico.invalidDateRange'));
                return;
            }
            this.loading = true;
            try {
                const params = {};
                if (this.idUsuario != null) params.id_usuario = this.idUsuario;
                if (this.idCercaDestino != null && String(this.idCercaDestino).trim() !== '') {
                    params.id_cerca_destino = this.idCercaDestino;
                }
                if (this.idUnidade != null && String(this.idUnidade).trim() !== '') {
                    params.id_unidade = this.idUnidade;
                }
                if (this.dateFrom) params.dti = this.dateFrom;
                if (this.dateTo) params.dtf = this.dateTo;
                const res = await apiController.request(apiController.endpoints.getHistoricoroteirizacao, {
                    query: params,
                    silent: true,
                });
                const rows = Array.isArray(res.data) ? res.data : [];
                this.items = rows.map((row) => this.mapHistoricoItem(row));
                this.selected = [];
                if (this.hasCurrentHistorico) {
                    await this.resolveLatestHistoricoId();
                }
            } catch (e) {
                this.items = [];
            } finally {
                this.loading = false;
            }
        },
        confirmLoad() {
            const row = this.selected && this.selected[0];
            if (!row || row.id_historico_roteirizacao == null) return;
            this.pendingMode = 'id';
            this.pendingId = row.id_historico_roteirizacao;
            this.pendingRecord = null;
            if (this.hasRoutingSolution) {
                this.confirmReplace = true;
                return;
            }
            this.doLoadById();
        },
        confirmLoadLatest() {
            if (!this.canLoadLatest) return;
            this.pendingMode = 'latest';
            this.pendingId = null;
            this.pendingRecord = null;
            if (this.hasRoutingSolution) {
                this.confirmReplace = true;
                return;
            }
            this.doLoadLatest();
        },
        cancelConfirmReplace() {
            this.confirmReplace = false;
            this.pendingMode = null;
            this.pendingId = null;
            this.pendingRecord = null;
        },
        doConfirmReplace() {
            this.confirmReplace = false;
            if (this.pendingMode === 'latest') {
                this.doLoadLatest();
                return;
            }
            this.doLoadById();
        },
        async doLoadById() {
            const id = this.pendingId;
            if (id == null) return;
            this.loadingDetail = true;
            try {
                const res = await apiController.request(apiController.endpoints.getHistoricoroteirizacaoporid, {
                    params: { id },
                    silent: true,
                });
                if (res.data && res.data.erro) {
                    this.$emit('error', res.data.mensagem || this.$t('routePlanning.dialogs.historico.loadError'));
                    return;
                }
                this.$emit('loaded', res.data);
                this.close();
            } catch (e) {
                this.$emit('error', this.$t('routePlanning.dialogs.historico.loadError'));
            } finally {
                this.loadingDetail = false;
                this.pendingId = null;
                this.pendingMode = null;
            }
        },
        async doLoadLatest() {
            if (!this.canLoadLatest) return;
            const destinoId = this.resolvedDestinoId;
            // Sem destino resolvido: carrega o registro mais recente já listado.
            if (destinoId == null) {
                const first = this.items && this.items[0];
                if (!first || first.id_historico_roteirizacao == null) {
                    this.$emit('error', this.$t('routePlanning.dialogs.historico.loadLatestEmpty'));
                    return;
                }
                this.pendingMode = 'id';
                this.pendingId = first.id_historico_roteirizacao;
                await this.doLoadById();
                return;
            }
            this.loadingLatest = true;
            try {
                const query = { id_cerca_destino: destinoId };
                if (this.idUnidade != null && String(this.idUnidade).trim() !== '') {
                    query.id_unidade = this.idUnidade;
                }
                if (this.idUsuario != null) query.id_usuario = this.idUsuario;
                const res = await apiController.request(apiController.endpoints.getUltimohistoricoroteirizacao, {
                    query,
                    silent: true,
                });
                if (res.data && res.data.erro) {
                    this.$emit('error', res.data.mensagem || this.$t('routePlanning.dialogs.historico.loadLatestEmpty'));
                    return;
                }
                if (!res.data || res.data.id_historico_roteirizacao == null) {
                    this.$emit('error', this.$t('routePlanning.dialogs.historico.loadLatestEmpty'));
                    return;
                }
                this.$emit('loaded', res.data);
                this.close();
            } catch (e) {
                const status = e && e.response && e.response.status;
                if (status === 404) {
                    this.$emit('error', this.$t('routePlanning.dialogs.historico.loadLatestEmpty'));
                } else {
                    this.$emit('error', this.$t('routePlanning.dialogs.historico.loadError'));
                }
            } finally {
                this.loadingLatest = false;
                this.pendingMode = null;
            }
        },
    },
};
</script>
